import React, { useMemo } from 'react';
import { Route, Switch, useLocation, useParams } from 'react-router-dom';
import cx from 'classnames';

import {
  createSportsbookState,
  get,
  PreferencesProvider,
  SelectedSportsbookSportProvider,
  SiteConfigManager,
  SportsbookProvider,
  useCreatePreferencesState,
  useSettings,
} from '@atlas/core';
import { COMPONENT_TYPES, SportsbookRouter, sportsbookRoutes } from '@atlas/routing';
import { ScrollableContainer } from '@atlas/ui';

import { useLocalBetslip } from 'common/Betslip/LocalBetslipContext';
import { SIMPLIFIED_SPORTSBOOK_PATH_PATTERN } from 'common/constants';
import SportsList from 'common/Widgets/SportsList/SportsList';
import TopEvents from 'common/Widgets/TopEvents';
import SportsbookFavorites from 'components/FavoriteEvents/SportsbookFavorites';
import FreeBetNotification from 'components/FreeBetNotification/FreeBetNotification';
import SportsbookMaintenanceContainer from 'components/MaintenanceContainer/SportsbookMaintenanceContainer';
import EmptySportView from 'components/Pages/EmptySportView';
import LiveCategoriesView from 'components/Pages/Live/LiveCategoriesView';
import LiveSportsbookLoader from 'components/Pages/LiveSportsbookLoader';
import SimplifiedSportsbookView from 'components/Pages/Prematch/SimplifiedSportsbookView';
import SportLoader from 'components/Pages/Prematch/SportLoader';
import {
  SelectedEventQueryRouteWrapper,
  SelectedEventQueryViewWrapper,
  SelectedProviderWrapper,
} from 'components/Pages/SelectedWrappers';
import SportItemDestruction from 'components/Pages/SportItemDestruction';
import { DefaultSportsbookNavigation } from 'components/Pages/SportsbookNavigation';
import { CategoryRedirect, SportRedirect } from 'components/Pages/SportsbookRedirects';

const { SPORT_LIVE } = COMPONENT_TYPES;

import { getFlyingBetslipConfig, sportsbookRouterListenerCb } from 'helpers/prematch';

import DetailsPage from 'components/Pages/Details';

const LiveSportsbookProvider = props => {
  const { children } = props;

  const { categoryName, sportName, tournamentName } = useParams();

  const sportsbookState = createSportsbookState({
    id: 'live',
    selectedCategoryUrlName: categoryName,

    selectedSportUrlName: sportName,
    selectedTournamentUrlName: tournamentName,
    sportService: 'LIVE',
  });

  return <SportsbookProvider value={sportsbookState}>{children}</SportsbookProvider>;
};

const LiveSportsbookView = () => {
  const { footerHeight, footerUrl, quickBet: isQuickBet } = useSettings();
  const { betIdToEdit } = useLocalBetslip();
  const location = useLocation();

  const { topEvents } = SiteConfigManager.getConfig('config.pages').find(
    page => page.type === SPORT_LIVE,
  ).options;

  const livePath = get(
    SiteConfigManager.getConfig('config.pages', []).find(({ type }) => type === SPORT_LIVE),
    'path',
  );

  const layoutToScrollClass = useMemo(
    () =>
      cx('layout-to-scroll full-height', {
        'layout-to-scroll-with-padding': getFlyingBetslipConfig().hasExtended,
        quickbet: isQuickBet || betIdToEdit,
      }),
    [betIdToEdit, isQuickBet],
  );

  const footerIframe = useMemo(() => {
    if (footerUrl) {
      const options = {
        frameBorder: '0',
        src: footerUrl,
        width: '100%',
      };

      if (footerHeight) {
        options.height = footerHeight;
      }

      return <iframe {...options} />;
    }

    return null;
  }, [footerUrl, footerHeight]);

  const content = (
    <LiveSportsbookLoader>
      <ScrollableContainer className={layoutToScrollClass} id="content">
        <FreeBetNotification />
        {topEvents && <TopEvents />}

        <Switch>
          <Route path={`${livePath}/:sportName/:categoryName?`}>
            <DefaultSportsbookNavigation path={livePath}>
              <span className="sport-scroll" />
              <SportsList />

              <div className="sportsbook-content">
                <SelectedProviderWrapper Component={SelectedSportsbookSportProvider}>
                  <Switch>
                    <Route
                      component={SportsbookFavorites}
                      path={`${livePath}/${sportsbookRoutes.favorites}`}
                    />

                    <EmptySportView>
                      <Route path={`${livePath}/:sportName/:categoryName/:tournamentName?`}>
                        <SportLoader className="absolute">
                          <Route
                            component={LiveCategoriesView}
                            exact
                            path={`${livePath}/:sportName/:categoryName`}
                          />
                        </SportLoader>
                      </Route>
                      <Route>
                        <SportLoader>
                          <CategoryRedirect />
                          <SportItemDestruction />
                        </SportLoader>
                      </Route>
                    </EmptySportView>
                  </Switch>
                </SelectedProviderWrapper>
              </div>

              {footerIframe}
            </DefaultSportsbookNavigation>
          </Route>
          <SportRedirect />
        </Switch>
      </ScrollableContainer>
    </LiveSportsbookLoader>
  );

  return (
    <SelectedEventQueryViewWrapper fallback={content} location={location}>
      <SelectedEventQueryRouteWrapper>
        <DetailsPage />
      </SelectedEventQueryRouteWrapper>
    </SelectedEventQueryViewWrapper>
  );
};

const LiveSportsbook = () => {
  const favoritesState = useCreatePreferencesState();

  const isSimplifiedView = SiteConfigManager.getConfig(
    'config.config.sportsbook.mobileGridViewType',
  );

  const props = {};
  if (isSimplifiedView === 'simplified') {
    props.path = SIMPLIFIED_SPORTSBOOK_PATH_PATTERN;
  }

  return (
    <SportsbookMaintenanceContainer>
      <SportsbookRouter
        isListenRouterNavigation
        sportsbookRouterListenerCb={sportsbookRouterListenerCb}
        {...props}
      >
        <PreferencesProvider value={favoritesState}>
          <LiveSportsbookProvider>
            {isSimplifiedView === 'simplified' ? (
              <SimplifiedSportsbookView path="/live" />
            ) : (
              <LiveSportsbookView />
            )}
          </LiveSportsbookProvider>
        </PreferencesProvider>
      </SportsbookRouter>
    </SportsbookMaintenanceContainer>
  );
};

export default LiveSportsbook;
